import { Team } from "@/model/Team"
import { Player } from "@/model/Player"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Star } from "lucide-react"

interface TopPerformersProps {
  teams: Team[]
}

interface ExtendedPlayer extends Player {
  adjustedPoints: number
}

const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'DST', 'K']

export function TopPerformers({ teams }: TopPerformersProps) {
  // Flatten all players from all teams and group by position
  const playersByPosition: Record<string, ExtendedPlayer[]> = teams.flatMap(team =>
    team.players.map(player => ({
      ...player,
      adjustedPoints: player.id === team.mvp.id ? player.points * 1.5 : player.points
    } as ExtendedPlayer))
  ).reduce((acc, player) => {
    if (!acc[player.position]) {
      acc[player.position] = []
    }
    acc[player.position].push(player)
    return acc
  }, {} as Record<string, ExtendedPlayer[]>)

  // Get top performer for each position
  const topPerformersByPosition = POSITION_ORDER.reduce((acc: Record<string, ExtendedPlayer[]>, position) => {
    if (playersByPosition[position]) {
      acc[position] = playersByPosition[position]
        .sort((a, b) => b.adjustedPoints - a.adjustedPoints)
        .slice(0, 1)
    }
    return acc
  }, {})

  return (
    <Card>
      <CardHeader>
        <CardTitle>⭐ Top Performers</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {POSITION_ORDER.map(position => {
            const performers = topPerformersByPosition[position]
            if (!performers || performers.length === 0) return null

            return performers.map((player, index) => (
              <div key={player.id} className="flex items-center justify-between py-1 px-2 rounded bg-card/30">
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <span className="text-xs font-medium text-muted-foreground shrink-0 w-8">
                    {position}
                  </span>
                  <span className="text-xs truncate flex-1">{player.name}</span>
                  {player.adjustedPoints > player.points && (
                    <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                  )}
                  <span className="text-xs text-muted-foreground shrink-0">
                    {player.nflTeam}
                  </span>
                </div>
                <span className="text-xs font-medium shrink-0 ml-2">
                  {player.adjustedPoints.toFixed(1)}
                </span>
              </div>
            ))
          })}
        </div>
      </CardContent>
    </Card>
  )
}
